/**
 * Global Ask AI shell — keeps the Guide drawer mounted once and exposes
 * `openAskAi()` to the header, sidebar and legacy `/ask-ai` redirects.
 */
import * as React from "react";
import { AskAiDrawer } from "@/components/shell/AskAiDrawer";

interface AskAiContextValue {
  openAskAi: () => void;
}

const AskAiContext = React.createContext<AskAiContextValue | null>(null);

export function AskAiProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const openAskAi = React.useCallback(() => setOpen(true), []);
  // Memoised like every other context value in the app. Two consumers and a
  // stable `openAskAi` make the cost of the object literal about nothing — this
  // is for consistency, so a reader does not have to work out whether this one
  // was deliberate.
  const value = React.useMemo<AskAiContextValue>(() => ({ openAskAi }), [openAskAi]);

  return (
    <AskAiContext.Provider value={value}>
      {children}
      <AskAiDrawer open={open} onOpenChange={setOpen} />
    </AskAiContext.Provider>
  );
}

export function useAskAi(): AskAiContextValue {
  const ctx = React.useContext(AskAiContext);
  if (!ctx) {
    throw new Error("useAskAi must be used within AskAiProvider");
  }
  return ctx;
}
